// Main emotion cloud assets
import sunnyCloud from '../../assets/icons/check-in/sunny-cloud.svg';
import hazyCloud from '../../assets/icons/check-in/hazy-cloud.svg';
import rainyCloud from '../../assets/icons/check-in/rainy-cloud.svg';
import stormyCloud from '../../assets/icons/check-in/stormy-cloud.svg';
import happyCloud from '../../assets/icons/check-in/happy-cloud.svg';

// Sunny sub-emotion assets
import fineCloudEmo from '../../assets/icons/check-in/fine-cloud-emo.svg';
import confidentCloudEmo from '../../assets/icons/check-in/confident-cloud-emo.svg';
import focusedCloudEmo from '../../assets/icons/check-in/focused-cloud-emo.svg';
import happyCloudEmo from '../../assets/icons/check-in/happy-cloud-emo.svg';
import hopefulCloudEmo from '../../assets/icons/check-in/hopeful-cloud-emo.svg';
import proudCloudEmo from '../../assets/icons/check-in/proud-cloud-emo.svg';
import peacefulCloudEmo from '../../assets/icons/check-in/peaceful-cloud-emo.svg';

// Hazy sub-emotion assets
import nervousCloudEmo from '../../assets/icons/check-in/nervous-cloud-emo.svg';
import confusedCloudEmo from '../../assets/icons/check-in/confused-cloud-emo.svg';
import worriedCloudEmo from '../../assets/icons/check-in/worried-cloud-emo.svg';
import sillyCloudEmo from '../../assets/icons/check-in/silly-cloud-emo.svg';
import irritatedCloudEmo from '../../assets/icons/check-in/irritated-cloud-emo.svg';
import excitedCloudEmo from '../../assets/icons/check-in/excited-cloud-emo.svg';
import embarrassedCloudEmo from '../../assets/icons/check-in/embarrassed-cloud-emo.svg';

// Stormy sub-emotion assets
import angryCloudEmo from '../../assets/icons/check-in/angry-cloud-emo.svg';
import agitatedCloudEmo from '../../assets/icons/check-in/agitated-cloud-emo.svg';
import devastatedCloudEmo from '../../assets/icons/check-in/devastated-cloud-emo.svg';
import frightenedCloudEmo from '../../assets/icons/check-in/frightened-cloud-emo.svg';
import jealousCloudEmo from '../../assets/icons/check-in/jealous-cloud-emo.svg';
import stressedOutCloudEmo from '../../assets/icons/check-in/stressed-out-cloud-emo.svg';
import furiousCloudEmo from '../../assets/icons/check-in/furious-cloud-emo.svg';

// Rainy sub-emotion assets
import sadCloudEmo from '../../assets/icons/check-in/sad-cloud-emo.svg';
import boredCloudEmo from '../../assets/icons/check-in/bored-cloud-emo.svg';
import disappointedCloudEmo from '../../assets/icons/check-in/disappointed-cloud-emo.svg';
import lonelyCloudEmo from '../../assets/icons/check-in/lonely-cloud-emo.svg';
import shyCloudEmo from '../../assets/icons/check-in/shy-cloud-emo.svg';
import sickCloudEmo from '../../assets/icons/check-in/sick-cloud-emo.svg';
import tiredCloudEmo from '../../assets/icons/check-in/tired-cloud-emo.svg';

export interface SubEmotion {
  name: string;
  label: string; // uppercase display label
  asset: string;
}

export interface CategoryData {
  label: string;
  name: string;
  cloudAsset: string;
  subtitle: string;
  descriptor: string;
  tip: string;
  why: string;
  subEmotions: SubEmotion[];
}

export const splashCloudAsset = happyCloud;

export const categories: Record<string, CategoryData> = {
  sunny: {
    label: 'sunny',
    name: 'Sunny Zone',
    cloudAsset: sunnyCloud,
    subtitle: "Feeling good! Let's share that sunshine.",
    descriptor: '(HAPPY, CALM)',
    tip: 'When your child feels happy and calm, their nervous system is regulated, this is the ideal window for learning, connection, and play. These moments are worth noticing and building on.',
    why: 'A regulated nervous system is the best time for connection, learning, and play. These activities build on that window.',
    subEmotions: [
      { name: 'Fine', label: 'FINE', asset: fineCloudEmo },
      { name: 'Confident', label: 'CONFIDENT', asset: confidentCloudEmo },
      { name: 'Peaceful', label: 'PEACEFUL', asset: peacefulCloudEmo },
      { name: 'Hopeful', label: 'HOPEFUL', asset: hopefulCloudEmo },
      { name: 'Proud', label: 'PROUD', asset: proudCloudEmo },
      { name: 'Happy', label: 'HAPPY', asset: happyCloudEmo },
      { name: 'Focused', label: 'FOCUSED', asset: focusedCloudEmo },
    ],
  },
  hazy: {
    label: 'hazy',
    name: 'Hazy Zone',
    cloudAsset: hazyCloud,
    subtitle: "Lots of energy! Let's use it",
    descriptor: '(WORRIED, UNSURE)',
    tip: `Tip: A buzzy or worried feeling means your child's nervous system is revved up. They're not being difficult, their body is asking for movement or sensory input to release the extra energy. Follow their lead.`,
    why: 'A revved-up nervous system needs movement or sensory input to release the extra energy before calm is possible.',
    subEmotions: [
      { name: 'Nervous', label: 'NERVOUS', asset: nervousCloudEmo },
      { name: 'Confused', label: 'CONFUSED', asset: confusedCloudEmo },
      { name: 'Embarrassed', label: 'EMBARRASSED', asset: embarrassedCloudEmo },
      { name: 'Excited', label: 'EXCITED', asset: excitedCloudEmo },
      { name: 'Irritated', label: 'IRRITATED', asset: irritatedCloudEmo },
      { name: 'Silly', label: 'SILLY', asset: sillyCloudEmo },
      { name: 'Worried', label: 'WORRIED', asset: worriedCloudEmo },
    ],
  },
  stormy: {
    label: 'stormy',
    name: 'Stormy Zone',
    cloudAsset: stormyCloud,
    subtitle: "Big feelings right now. Let's ride it out together.",
    descriptor: '(ANGRY, OVERWHELMED)',
    tip: `Big feelings like anger or fear flood the body with stress hormones. Your child can't think their way out, they need to move it out first. Stay close, stay calm. Your regulated presence is the most powerful tool you have.`,
    why: 'Physical release moves stress hormones through the body safely. The nervous system is in overdrive, it needs co-regulation and a way to release big feelings before calm language is possible.',
    subEmotions: [
      { name: 'Angry', label: 'ANGRY', asset: angryCloudEmo },
      { name: 'Agitated', label: 'AGITATED', asset: agitatedCloudEmo },
      { name: 'Furious', label: 'FURIOUS', asset: furiousCloudEmo },
      { name: 'Stressed Out', label: 'STRESSED OUT', asset: stressedOutCloudEmo },
      { name: 'Jealous', label: 'JEALOUS', asset: jealousCloudEmo },
      { name: 'Frightened', label: 'FRIGHTENED', asset: frightenedCloudEmo },
      { name: 'Devastated', label: 'DEVASTATED', asset: devastatedCloudEmo },
    ],
  },
  rainy: {
    label: 'rainy',
    name: 'Rainy Zone',
    cloudAsset: rainyCloud,
    subtitle: "Feeling low or sad. That's okay. Let's be gentle.",
    descriptor: '(SAD, LOW)',
    tip: 'When your child feels sad, low, or withdrawn, their nervous system is slowed down. They need comfort, warmth, and gentle connection, not fixing or cheering up. Just being beside them is enough.',
    why: 'Quiet presence and gentle touch are the most powerful co-regulation tools. These activities slow everything down and signal safety.',
    subEmotions: [
      { name: 'Sad', label: 'SAD', asset: sadCloudEmo },
      { name: 'Bored', label: 'BORED', asset: boredCloudEmo },
      { name: 'Tired', label: 'TIRED', asset: tiredCloudEmo },
      { name: 'Sick', label: 'SICK', asset: sickCloudEmo },
      { name: 'Shy', label: 'SHY', asset: shyCloudEmo },
      { name: 'Lonely', label: 'LONELY', asset: lonelyCloudEmo },
      { name: 'Disappointed', label: 'DISAPPOINTED', asset: disappointedCloudEmo },
    ],
  },
};

/**
 * Get category data by label. Returns undefined if not found.
 */
export function getCategoryData(label: string): CategoryData | undefined {
  return categories[label];
}

/**
 * Find a sub-emotion by name within a category.
 */
export function getSubEmotion(
  categoryLabel: string,
  subEmotionName: string
): SubEmotion | undefined {
  const category = categories[categoryLabel];
  if (!category) return undefined;
  return category.subEmotions.find(
    (se) => se.name.toLowerCase() === subEmotionName.toLowerCase()
  );
}
